import { Wallet } from '@src/reducers/wallet.reducer'
import { CurrencyList } from '@src/service/coins/coins.types'
import ConfigurationService from '@src/service/ConfigurationService'
import { createContext, useContext, useState, ReactNode } from 'react'
import { DepositViewComponentDepositAddressQuery$data } from './__generated__/DepositViewComponentDepositAddressQuery.graphql'
import { DepositViewTokensQuery$data } from './__generated__/DepositViewTokensQuery.graphql'

export enum WalletView {
  deposit,
  externalDeposit,
  withdraw,
  history,
  list,
}

interface WalletContextType {
  isBack: boolean
  lastWalletView: WalletView
  walletView: WalletView
  isListView: boolean
  setIsBack: (value: boolean) => void
  setLastWalletView: (view: WalletView) => void
  setWalletView: (view: WalletView) => void
  setIsListView: (value: boolean) => void
  selectedExternalCoin?: { coin?: string; token?: string; chain: string }
  setSelectedExternalCoin: (value: { coin?: string; token?: string; chain: string }) => void
  isExternalCurrency: boolean
  depositAddressData?: DepositViewComponentDepositAddressQuery$data
  setDepositAddressData: (value: DepositViewComponentDepositAddressQuery$data | undefined) => void
  tokens?: DepositViewTokensQuery$data
  setTokens: (value: DepositViewTokensQuery$data) => void
  coin?: DepositViewTokensQuery$data['tokens'][0]
  paymentOrder?: { address: string; memo: string | null }
  setPaymentOrder: (value: { address: string; memo: string | null }) => void
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

export const WalletProvider = ({ children, activeWallet }: { children: ReactNode; activeWallet: Wallet }) => {
  const [isBack, setIsBack] = useState(false)
  const [lastWalletView, setLastWalletView] = useState(WalletView.deposit)
  const [walletView, setWalletView] = useState(WalletView.deposit)
  const [isListView, setIsListView] = useState(false)
  const [selectedExternalCoin, setSelectedExternalCoin] = useState<{ coin?: string; token?: string; chain: string }>()
  const isExternalCurrency = !!ConfigurationService.instance.isExternal({ key: activeWallet?.currency })
  const [depositAddressData, setDepositAddressData] = useState<
    DepositViewComponentDepositAddressQuery$data | undefined
  >(undefined)
  const [tokens, setTokens] = useState<DepositViewTokensQuery$data>()
  const coin = tokens?.tokens.find((x) => x.coinId === selectedExternalCoin?.coin)
  const [paymentOrder, setPaymentOrder] = useState<{ address: string; memo: string | null }>()

  return (
    <WalletContext.Provider
      value={{
        isBack,
        setIsBack,
        lastWalletView,
        setLastWalletView,
        walletView,
        setWalletView,
        isListView,
        setIsListView,
        selectedExternalCoin,
        setSelectedExternalCoin,
        isExternalCurrency,
        depositAddressData,
        setDepositAddressData,
        tokens,
        setTokens,
        coin,
        paymentOrder,
        setPaymentOrder,
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export const useModalHook = () => {
  const context = useContext(WalletContext)
  if (!context) {
    throw new Error('useModalHook must be used within a WalletProvider')
  }
  return context
}
